/**
 * Payment-Link Link-Tester — Format-Definition (Spec 501, Phase 1).
 *
 * Die zentrale Design-Naht: der EINZIGE Ort, an dem format-spezifisches Wissen
 * sitzt. Das Geruest kennt die FORM dieser Definition, nicht ihre WERTE. Solange
 * die Facade das Link-Format (kanonische Reihenfolge, signiert/frei-Zuordnung,
 * Signatur-Parameter, Testvektor) nicht vertraglich geliefert hat, laeuft das
 * Werkzeug mit `PENDING_FORMAT` (`available:false`) als Teaser — NIE mit
 * geratenen Werten (Spec §Problem Statement, §Teaser-Verhalten).
 *
 * Ein spaeterer Increment ersetzt `PENDING_FORMAT` durch die echte, gelieferte
 * Definition — ohne die Geruest-Architektur zu aendern.
 */

/** Ein Link-Parameter, wie er durch das Werkzeug fliesst. */
export interface LinkParam {
  key: string;
  value: string;
}

/**
 * Klassifikation eines Parameters durch das Format. `signed` geht in die Signatur
 * ein, `free` (z. B. UTM-Tracking) nicht, `unknown` = ohne geladenes Format nicht
 * entscheidbar (Teaser-Zustand).
 */
export type ParamClass = 'signed' | 'free' | 'unknown';

/**
 * Body-Reach eines Parameters — die von Spec 503 korrigierte Klassifikations-
 * Dimension (Handover §1). Unter „c=1" wird JEDER Parameter außer `sig` signiert;
 * `BodyReach` sagt, ob und wie er den Facade-Body erreicht:
 * - `systemField`  System-Whitelist → mappt auf ein Body-Feld,
 * - `meta`         `meta_<x>` → `metadata.<x>`,
 * - `routing`      `m`/`c` → signiert & Pflicht, aber KEIN Body-Feld,
 * - `raw`          eigener roher Parameter → mitsigniert, erreicht die Facade nicht.
 */
export type BodyReach = 'systemField' | 'meta' | 'routing' | 'raw';

/**
 * WO der Signatur-Parameter in der URL sitzt (Spec §Format-Definition: „Name UND
 * Platzierung"). Die Platzierung ist format-gated; im Teaser `unknown`. Bewusst
 * jetzt Teil der Naht, damit der Format-Increment ein reiner Drop-in bleibt und
 * die Vorschau-/Parse-Logik spaeterer Phasen nicht das Interface aendern muss.
 * - `query-append`  Signatur als letzter Query-Parameter,
 * - `query-prepend` Signatur als erster Query-Parameter,
 * - `unknown`       ohne geladenes Format nicht bekannt.
 */
export type SignaturePlacement = 'query-append' | 'query-prepend' | 'unknown';

/** Eingaben fuer kanonischen Bau und Fingerprinting. */
export interface SignatureInputs {
  /** Vorbefuellte System-Parameter (aus dem Service-Prefill). */
  systemParams: LinkParam[];
  /** Freie Nutzer-Parameter (UTM etc.). */
  freeParams: LinkParam[];
}

/**
 * Service-Kontext fuer die Ableitung der System-Parameter (Prefill). Welche
 * Felder auf welche Parameter-Keys abgebildet werden, ist format-gated — daher
 * liefert die Format-Definition die Zuordnung (`buildSystemParams`), nicht das
 * Geruest.
 */
export interface ServicePrefill {
  serviceId: string;
  merchantId: string;
  grossAmountCents?: number;
  interval?: string;
  displayName?: string;
}

/**
 * Ergebnis einer Fehler-Transformation: eine alternative Berechnungsgrundlage.
 * `stringToSign` ersetzt die Nachricht, `key` den Schluessel (z. B. der
 * hex-dekodierte Salt in V8). `null` = Transformation auf diese Eingaben nicht
 * anwendbar (wird uebersprungen).
 */
export interface TransformResult {
  stringToSign?: string;
  key?: string;
  /**
   * Roher Byte-Schlüssel (hat Vorrang vor `key`). Für Fehler-Varianten, deren
   * Schlüssel NICHT als Text darstellbar ist — v. a. der hex-dekodierte Salt, dessen
   * Bytes ≥ 0x80 als UTF-8-Text verfälscht würden (Spec 503 Integrations-Review P1).
   */
  keyBytes?: Uint8Array;
}

/**
 * Eine Fehler-Transformation fuer das Fingerprinting (Spec §Fingerprint-Katalog).
 * Datengetrieben: `transform` erzeugt aus den Eingaben eine alternative
 * String-to-Sign/Schluessel-Kombination; das Framework rechnet sie durch und
 * meldet `diagnoseKey` (i18n-Key der Diagnose-Copy), wenn sie die gelieferte
 * Signatur trifft. Die konkreten Varianten V1–V12 kommen mit der Format-Definition.
 */
export interface FingerprintTransform {
  id: string;
  diagnoseKey: string;
  transform: (
    inputs: SignatureInputs,
    canonicalKey: string,
  ) => TransformResult | null;
}

/**
 * Offizieller Testvektor — zugleich die Demo-Daten. Kommt mit der Format-Definition
 * (Spec §Onboarding-Schicht); dient dem Engine-Selbsttest, sobald geliefert.
 */
export interface FormatTestVector {
  salt: string;
  systemParams: LinkParam[];
  freeParams: LinkParam[];
  expectedSignature: string;
}

/**
 * Format-Definition. Alle „scharfen" Methoden setzen `available:true` voraus;
 * die UI gated darauf und ruft sie im Teaser nicht auf.
 */
export interface PaymentLinkFormatDefinition {
  /** Ob eine echte Definition geladen ist. Steuert Teaser vs. scharf. */
  readonly available: boolean;
  /** Name des Signatur-Query-Parameters (z. B. `sig`). Leer im Teaser. */
  readonly signatureParamName: string;
  /** Platzierung des Signatur-Parameters in der URL. `unknown` im Teaser. */
  readonly signaturePlacement: SignaturePlacement;
  /**
   * Klick-Endpunkt-Pfad des Dienstes (Format-Wissen, Handover §1: `/link/v1`).
   * Wird an die Stage-Basis gehängt, damit gebaute Links tatsächlich routen
   * (`pay.<stage>…/link/v1?…`). Die Signatur bleibt pfad-frei. Leer/undefined im
   * Teaser oder wenn der Pfad die Basis-URL bereits enthält.
   */
  readonly clickPath?: string;
  /** Pflicht-System-Parameter (Keys). Leer im Teaser. */
  readonly requiredParams: readonly string[];
  /** Fehler-Transformationen fuer das Fingerprinting. Leer im Teaser. */
  readonly transforms: readonly FingerprintTransform[];
  /** Optionaler offizieller Testvektor / Demo-Daten. */
  readonly testVector?: FormatTestVector;
  /** Kanonischer String-to-Sign in fester Reihenfolge. Wirft im Teaser. */
  buildStringToSign(inputs: SignatureInputs): string;
  /**
   * Leitet die (gesperrten, vorbefuellten) System-Parameter aus dem Service-
   * Prefill ab — die Zuordnung Prefill→Parameter-Key ist format-gated. Wirft im
   * Teaser (die UI gated darauf und zeigt „Format offen").
   */
  buildSystemParams(prefill: ServicePrefill): LinkParam[];
  /** Klassifiziert einen Parameter-Key. Im Teaser: `unknown`. */
  classifyParam(key: string): ParamClass;
  /**
   * Body-Reach eines Parameters (Spec 503) — speist die 4 Herkunfts-/Wirkungs-
   * Badges und die Body-Reach-Segmentierung der Vorschau. Optional: der Teaser
   * (`PENDING_FORMAT`) implementiert ihn nicht (dort ist keine Zuordnung
   * entscheidbar); Konsumenten fallen dann auf `classifyParam`/`unknown` zurück.
   */
  bodyReachOf?(key: string): BodyReach;
  /**
   * Zielfeld im Facade-Body für die „→ Body-Feld X"-Badges (Spec 503): das
   * gemappte Body-Feld für System-Keys, `metadata.<x>` für `meta_`-Keys, sonst
   * `undefined` (Routing/roh erreichen den Body nicht). Optional wie `bodyReachOf`.
   */
  bodyFieldOf?(key: string): string | undefined;
}

/**
 * Fehler, wenn eine scharfe Methode ohne geladene Format-Definition aufgerufen
 * wird. Bewusst „fail loud": lieber ein typisierter, abfangbarer Fehler, der die
 * UI in den Teaser-Hinweis fuehrt, als eine erfundene Signatur, die eine falsche
 * Bau-Variante als „richtig" bestaetigt (Spec §Problem Statement).
 */
export class FormatNotAvailableError extends Error {
  constructor(method: string) {
    // Internal developer-facing invariant error (never user-visible — the UI gates
    // on `available` and never calls sharp methods in the teaser). Kept in English
    // so it does not trip the hardcoded-German i18n guard and needs no t() wiring.
    super(
      `Payment-Link format definition is not loaded yet (${method}). ` +
        `Format specification requested.`,
    );
    this.name = 'FormatNotAvailableError';
  }
}

/**
 * Der Teaser-Zustand: keine Werte, keine Signatur. Scharfe Methoden werfen
 * `FormatNotAvailableError`; `classifyParam` liefert `unknown`. Die UI degradiert,
 * indem sie auf `available` gated und diese Methoden gar nicht erst aufruft.
 */
export const PENDING_FORMAT: PaymentLinkFormatDefinition = {
  available: false,
  signatureParamName: '',
  signaturePlacement: 'unknown',
  requiredParams: [],
  transforms: [],
  testVector: undefined,
  buildStringToSign(): string {
    throw new FormatNotAvailableError('buildStringToSign');
  },
  buildSystemParams(): LinkParam[] {
    throw new FormatNotAvailableError('buildSystemParams');
  },
  classifyParam(): ParamClass {
    return 'unknown';
  },
};
